// src/pages/CamViewPage.tsx — CamView (admin / apps.camView only).
//
// A tile per NYMR station; tapping one pulls a live on-demand CCTV snapshot for
// that station (proxied through /api/camview/snapshot so the Hik-Connect signed
// URL never reaches the browser). Snapshots are fetched live and never stored.
// Because the API uses a cookie session, a plain <img src> to the snapshot
// endpoint authenticates automatically — a cache-busting nonce forces a fresh
// capture on refresh.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, PermissionLoading, TopBar } from "../components/ui";
import { Camera } from "../components/ui/Icon";

interface StationOut { id: string; name: string; camera: string; }
interface StationsResponse { configured: boolean; stations: StationOut[]; }

const nonce = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

export function CamViewPage() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useCurrentUser();
  const [data, setData] = useState<StationsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<StationOut | null>(null);
  const [imgNonce, setImgNonce] = useState<string>(nonce);
  const [imgState, setImgState] = useState<"loading" | "ok" | "error">("loading");

  const allowed = Boolean(me && (me.isAdmin || me.apps?.camView));

  useEffect(() => {
    if (!allowed) return;
    apiFetch<StationsResponse>("/api/camview/stations")
      .then((d) => { setData(d); setLoadError(null); })
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : "Couldn't load the station list."));
  }, [allowed]);

  const open = useCallback((s: StationOut) => {
    setSelected(s);
    setImgState("loading");
    setImgNonce(nonce());
  }, []);

  const refresh = useCallback(() => {
    setImgState("loading");
    setImgNonce(nonce());
  }, []);

  if (meLoading) return <PermissionLoading title="CamView" onBack={() => navigate("/apps")} />;

  if (!allowed) {
    return (
      <div>
        <TopBar title="CamView" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 6 }}>You don't have access to CamView.</div>
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, fontWeight: 600, lineHeight: 1.45 }}>Ask a system administrator to enable CamView for your account.</div>
            <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={() => navigate("/apps")}>Back to apps</Button>
          </Card>
        </div>
      </div>
    );
  }

  const snapshotUrl = selected ? `/api/camview/snapshot?station=${encodeURIComponent(selected.id)}&t=${imgNonce}` : null;

  return (
    <div>
      <TopBar title="CamView" sub="Live station cameras" onBack={() => navigate("/apps")} />
      <div style={{ padding: "14px 16px 28px", display: "flex", flexDirection: "column", gap: 14 }}>

        {loadError && (
          <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{loadError}</div></Card>
        )}

        {data && data.configured === false && (
          <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>CamView isn't configured yet — the CCTV connection credentials are missing.</div></Card>
        )}

        {/* ── Station picker ── */}
        <Card>
          <Eyebrow>Stations</Eyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 10 }}>
            {(data?.stations ?? []).map((s) => {
              const active = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => open(s)}
                  aria-label={`View live camera at ${s.name}`}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                    minHeight: 92, padding: "14px 10px", cursor: "pointer",
                    borderRadius: 12, border: `1px solid ${active ? C.heather : C.hairline}`,
                    background: active ? C.heatherBg : C.paperSoft,
                    boxShadow: active ? "inset 0 0 0 1px " + C.heather : "0 1px 2px rgba(43,32,20,0.06)",
                  }}
                >
                  <Camera s={26} c={active ? C.heatherDk : C.heather} />
                  <span style={{ fontFamily: F.body, fontWeight: 800, fontSize: 14, color: C.ink }}>{s.name}</span>
                </button>
              );
            })}
          </div>
        </Card>

        {/* ── Selected station snapshot ── */}
        {selected && snapshotUrl && (
          <Card>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink }}>{selected.name}</div>
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, fontWeight: 600 }}>{selected.camera}</div>
              </div>
              <Button variant="secondary" onClick={refresh} disabled={imgState === "loading"}>
                {imgState === "loading" ? "Capturing…" : "Refresh"}
              </Button>
            </div>

            <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#11100e", borderRadius: 10, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {imgState === "loading" && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#d8cfa8", fontFamily: F.body, fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>
                  Capturing a live snapshot…
                </div>
              )}
              {imgState === "error" && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 10, alignItems: "center", justifyContent: "center", color: "#e7dcc0", fontFamily: F.body, fontWeight: 700, fontSize: 13, padding: 16, textAlign: "center" }}>
                  <span>Couldn't get a snapshot from this camera right now.</span>
                  <Button variant="secondary" onClick={refresh}>Try again</Button>
                </div>
              )}
              {/* key forces a fresh <img> per nonce so onLoad/onError always fire */}
              <img
                key={imgNonce}
                src={snapshotUrl}
                alt={`Live snapshot — ${selected.name} (${selected.camera})`}
                onLoad={() => setImgState("ok")}
                onError={() => setImgState("error")}
                style={{ width: "100%", height: "100%", objectFit: "contain", display: imgState === "ok" ? "block" : "none" }}
              />
            </div>

            <div style={{ marginTop: 10, fontFamily: F.body, fontSize: 11, color: C.inkSoft, fontWeight: 600, lineHeight: 1.4 }}>
              Live snapshot, captured on demand. For indication only — Track Access safety processes must be followed at all times when accessing the operational railway.
            </div>
          </Card>
        )}

        {!selected && (
          <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, fontWeight: 600, textAlign: "center", padding: "8px 0" }}>
            Tap a station to see a live camera snapshot.
          </div>
        )}
      </div>
    </div>
  );
}
